/**
 * LabelPopover — 빠른 조건의 라벨 고르기 판(설계 FE-20260912 §W02 「라벨도 같은 패턴」).
 *
 * 옛 개념 판(ConceptPopover — 2026-09-13 개념 목록 창으로 대체되며 제거)과 같은 규칙: 연 항을 판 안 임시 선택으로 복사 ·
 * 후보 고르기는 거기에만 · [적용]에서만 onApply(검색은 작업 칸의 자동 검색) ·
 * Escape·바깥 누름은 적용 없이 닫기 · Enter 는 활성 후보를 고르고 풀기만 · 방식은 연 항의 것을 유지(새 항은 하나라도).
 *
 * 후보는 GET /api/v2/labels?q= 로 찾는다(LabelPicker 와 같은 0.25초 디바운스 + 세대 번호). 이 요청의 로딩·오류는 판 안에서만
 * 보인다 — 문제 검색 상태와 섞지 않는다(설계 §W02). 판이 닫힌 뒤 도착한 응답은 세대 번호로 버린다.
 *
 * 라벨 이름은 서버가 uuid → 이름을 주지 않아 이 브라우저가 기억한 것뿐이다. 이번 판에서 후보로 본 이름은 판 안에서 쓰고,
 * **적용할 때** 고른 라벨의 이름만 onRemember 로 넘긴다 — 닫고 버린 선택 때문에 바깥 상태가 움직이지 않게.
 * 모르는 라벨은 「라벨 이름 확인 필요 · 앞 8자」로 적고 줄의 title 에 전체 uuid 를 둔다(다른 라벨과 구별되게).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common';
import FloatingPanel from './FloatingPanel';
import { labelNameOf } from './LabelPicker';
import { unknownLabelText } from './names';
import { listLabels, describeSyllabusError } from './syllabusApi';
import { FIELDS, LIMITS, MATCH_LABEL } from './syllabusQuery';
import { useFocusSoon, useListKeyNav } from './useListKeyNav';

export const LABEL_DEBOUNCE_MS = 250;

export const LABEL_MATCH_HINT = {
  any: '고른 라벨 가운데 하나라도 붙은 문제',
  all: '고른 라벨이 모두 붙은 문제',
};

const selectCls = 'rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs focus:border-primary focus:outline-none';
const inputCls = 'w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none';
const optionCls = (active) => `flex w-full cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-left text-xs ${
  active ? 'bg-primary/10' : 'hover:bg-gray-50'
}`;
const boxCls = (on) => `flex size-4 shrink-0 items-center justify-center rounded border ${on ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'}`;
const closeCls = 'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';

const lower = (u) => String(u).toLowerCase();
const dedupe = (arr) => arr.filter((v, k) => arr.indexOf(v) === k);

function LabelPopover({
  anchorRef = null,
  term = null,
  labelNames = {},
  onRemember = null,
  onApply,
  onClose,
  sheet = false,
}) {
  const { getAccessToken } = useAuth();
  const [seed] = useState(() => dedupe((term?.values || []).map(lower)));
  const [picked, setPicked] = useState(seed);
  const [match, setMatch] = useState(() => (FIELDS.label.matches.includes(term?.match) ? term.match : 'any'));
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seen, setSeen] = useState({}); // 이번 판에서 후보로 본 이름 { uuid: name }
  const [note, setNote] = useState(null);
  const genRef = useRef(0);
  const inputRef = useRef(null);
  useFocusSoon(inputRef);

  // 닫힌 뒤 도착한 응답은 버린다
  useEffect(() => () => { genRef.current += 1; }, []);

  // 찾기 — 글자를 멈춘 뒤 0.25초. 늦게 온 응답이 새 결과를 덮지 않게 세대 번호로 거른다(async-boundary §2).
  // 열쇠는 앞뒤 공백을 뗀 글자다 — 「쎈」 뒤에 띄어쓰기만 쳐도 같은 요청을 한 번 더 쏘던 것을 막는다(§3)
  const s = q.trim();
  useEffect(() => {
    genRef.current += 1;
    const gen = genRef.current;
    if (!s) {
      setHits([]);
      setError(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(() => {
      listLabels({ q: s, perPage: 20 }, getAccessToken)
        .then((items) => {
          if (gen !== genRef.current) return;
          const list = Array.isArray(items) ? items : [];
          setHits(list);
          setError(null);
          setSeen((prev) => {
            const next = { ...prev };
            list.forEach((it) => { if (it?.label_uuid && it.label_name) next[lower(it.label_uuid)] = it.label_name; });
            return next;
          });
        })
        .catch((e) => { if (gen === genRef.current) { setHits([]); setError(describeSyllabusError(e)); } })
        .finally(() => { if (gen === genRef.current) setLoading(false); });
    }, LABEL_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [s, getAccessToken]);

  const nameOf = (u) => seen[u] || labelNameOf(labelNames, u) || null;
  const pickedSet = new Set(picked);

  // 검색어가 비면 「연 항의 값 + 이번에 고른 것」 — 풀어도 목록에 남아 다시 고를 수 있다. 찾는 중·실패면 후보 없음
  const rows = useMemo(() => {
    if (q.trim() === '') return dedupe([...seed, ...picked]).map((u) => ({ uuid: u, name: nameOf(u), count: null }));
    if (loading || error) return [];
    return hits
      .filter((h) => h && h.label_uuid)
      .map((h) => ({ uuid: lower(h.label_uuid), name: h.label_name || null, count: h.problem_count ?? null }));
  }, [q, seed, picked, hits, loading, error, seen, labelNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (u) => {
    const has = pickedSet.has(u);
    if (!has && picked.length >= LIMITS.label_values) {
      setNote(`라벨은 한 조건에 ${LIMITS.label_values}개까지입니다.`);
      return;
    }
    setNote(null);
    setPicked(has ? picked.filter((v) => v !== u) : [...picked, u]);
  };

  const nav = useListKeyNav({ count: rows.length, onPick: (i) => toggle(rows[i].uuid), resetKey: `${q}|${loading}|${hits.length}` });

  const canApply = picked.length > 0;
  const apply = () => {
    if (!canApply) return;
    const pairs = picked.filter((u) => seen[u]).map((u) => [u, seen[u]]);
    if (pairs.length && typeof onRemember === 'function') onRemember(pairs);
    onApply({ values: [...picked], match });
  };

  const searching = q.trim() !== '';

  return (
    <FloatingPanel
      anchorRef={anchorRef}
      onClose={onClose}
      sheet={sheet}
      label="라벨 고르기"
      initialFocus={false}
      className={sheet ? '' : 'w-80 max-w-[calc(100vw-1rem)]'}
    >
      <div className="space-y-2 p-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-bold text-gray-900">라벨 고르기</p>
          <button
            type="button"
            onClick={() => onClose('button')}
            className={closeCls}
          >
            <X className="size-3.5" /> 닫기
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
          <select value={match} onChange={(e) => setMatch(e.target.value)} aria-label="엮는 방식" className={selectCls}>
            {FIELDS.label.matches.map((m) => <option key={m} value={m}>{MATCH_LABEL[m]}</option>)}
          </select>
          <span>{LABEL_MATCH_HINT[match]}</span>
        </div>

        <div className="space-y-1">
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={nav.onKeyDown}
            role="combobox"
            aria-expanded={rows.length > 0}
            aria-controls={nav.listId}
            aria-autocomplete="list"
            aria-activedescendant={nav.active >= 0 ? nav.optionId(nav.active) : undefined}
            aria-label="라벨 이름으로 찾기"
            placeholder="라벨 이름으로 찾기"
            className={inputCls}
          />
          {searching && loading && <p className="px-1 py-2 text-xs text-gray-400">찾는 중…</p>}
          {searching && !loading && error && <p role="alert" className="px-1 py-2 text-xs text-red-600">{error}</p>}
          {searching && !loading && !error && rows.length === 0 && <p className="px-1 py-2 text-xs text-gray-400">찾는 라벨이 없습니다</p>}
          {!searching && rows.length === 0 && <p className="px-1 py-2 text-xs text-gray-400">라벨 이름으로 찾으세요</p>}
          {rows.length > 0 && (
            <div
              ref={nav.listRef}
              id={nav.listId}
              role="listbox"
              aria-multiselectable="true"
              aria-label={searching ? '라벨 후보' : '고른 라벨'}
              className="relative max-h-60 space-y-0.5 overflow-y-auto"
            >
              {rows.map((r, i) => {
                const on = pickedSet.has(r.uuid);
                return (
                  <div
                    key={r.uuid}
                    id={nav.optionId(i)}
                    role="option"
                    aria-selected={on}
                    title={r.uuid}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { nav.setActive(i); toggle(r.uuid); }}
                    className={optionCls(i === nav.active)}
                  >
                    <span className={boxCls(on)} aria-hidden="true">{on && <Check className="size-3" />}</span>
                    <span className={`min-w-0 flex-1 truncate ${r.name ? 'text-gray-700' : 'text-gray-500'}`}>{r.name || unknownLabelText(r.uuid)}</span>
                    {r.count != null && <span className="shrink-0 text-[10px] text-gray-400">{r.count}문제</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {note && <p className="text-[11px] text-amber-700">{note}</p>}

        <div className="flex items-end justify-between gap-2 border-t border-gray-100 pt-2">
          <div className="min-w-0 space-y-0.5 text-[11px] leading-snug text-gray-500">
            <p>고른 라벨 {picked.length}개</p>
            <p>Enter는 후보를 고르기만 합니다. 적용 전까지 행 조건은 그대로입니다.</p>
          </div>
          <Button size="xs" onClick={apply} disabled={!canApply} title={canApply ? undefined : '라벨을 하나 이상 고르세요'} className="shrink-0">
            적용
          </Button>
        </div>
      </div>
    </FloatingPanel>
  );
}

export default LabelPopover;
